import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

export const useSiteSettings = () => {
  return useQuery({
    queryKey: ['site-settings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('site_settings')
        .select('setting_key, setting_value');
      
      if (error) throw error;
      
      // Transformer les données en objet
      const settings: any = {};
      data?.forEach(item => {
        settings[item.setting_key] = typeof item.setting_value === 'string' 
          ? JSON.parse(item.setting_value)
          : item.setting_value;
      });
      
      return settings;
    },
  });
};

export const useUpdateSiteSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (settings: any) => {
      // Sauvegarder chaque paramètre individuellement avec upsert correct
      const promises = Object.entries(settings).map(([key, value]) =>
        supabase
          .from('site_settings')
          .upsert({
            setting_key: key,
            setting_value: JSON.stringify(value)
          }, {
            onConflict: 'setting_key',
            ignoreDuplicates: false
          })
      );
      
      const results = await Promise.all(promises);
      
      // Vérifier s'il y a des erreurs
      for (const result of results) {
        if (result.error) {
          throw new Error(`Erreur lors de la sauvegarde: ${result.error.message}`);
        }
      }
      
      return settings;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site-settings'] });
      toast({
        title: 'Paramètres sauvegardés',
        description: 'Les modifications ont été appliquées avec succès.',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Erreur',
        description: error.message || 'Impossible de sauvegarder les paramètres.',
        variant: 'destructive',
      });
    },
  });
};